import Image from 'next/image'
import { cn } from '@/lib/utils'

/**
 * Pieces shared by the console dock header and its command center.
 */

export interface DockUser {
  name?: string
  email?: string
  image?: string
}

/** Wallet amounts that light one more cell of the credit meter each. */
export const CREDIT_STEPS = [1, 5, 10, 25, 50, 100, 250, 500]

/** The store holds the formatted balance ("€1,234.56", en-IE): read the number back. */
export function parseBalance(formatted: string): number | null {
  const amount = parseFloat(formatted.replace(/,/g, '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(amount) ? amount : null
}

/** How many credit cells the balance fills, 0 to CREDIT_STEPS.length. */
export function creditLevel(formatted: string): number {
  const amount = parseBalance(formatted)
  if (amount === null) return 0
  return CREDIT_STEPS.filter((step) => amount >= step).length
}

/** Photo or initial inside a glossy profile cube (the cube styling comes from the parent). */
export function AvatarFace({ user, size = 40 }: { user: DockUser; size?: number }) {
  if (user.image) {
    return (
      <Image
        src={user.image}
        alt=""
        width={size}
        height={size}
        unoptimized
        className="relative h-full w-full object-cover"
      />
    )
  }
  return (
    <span
      className={cn('relative font-heading font-bold leading-none', size > 48 ? 'text-xl' : 'text-sm')}
    >
      {user.name?.trim().charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'P'}
    </span>
  )
}

/** Four rising bars: how much of the credit meter the balance fills. */
export function CreditCells({ level, ready }: { level: number; ready: boolean }) {
  // Four bars stand for the eight steps, two steps per bar.
  const lit = ready ? Math.ceil(level / 2) : 0
  return (
    <span aria-hidden className="dock-cells">
      {[4, 6, 9, 12].map((height, index) => (
        <span key={height} data-on={index < lit} style={{ height }} />
      ))}
    </span>
  )
}
